/**
 * Client for the HL7 data layer, THROUGH the cgt-backend proxy
 * (`/api/integrations/data-layer/:sourceId/*`, ClickUp z8nrz7c366). The browser
 * no longer dials data-layer hosts: it names an allowlisted source and the
 * backend does the fetching, holding the upstream token server-side.
 *
 * That collapses most of the divergences this file used to have from
 * `webhooksApi.ts` — requests now carry the normal `authHeaders()` (Cognito
 * bearer + tenant header) and a 401 ends the session via
 * `handleUnauthorized()`, because a 401 here IS a dead CGT session, not a
 * data-layer opinion. What survives, deliberately:
 *
 * 1. Every call has an abort timeout. A 1.5s poll must never stack hung
 *    requests. The proxy's own upstream timeouts sit just under these, so a
 *    dead source surfaces as the proxy's explicit 503/504 (an `http` failure
 *    naming the source) rather than as our abort.
 * 2. Two return disciplines. Polled reads throw `DataLayerError` so React
 *    Query owns the error state. Actions return an `Outcome` and never throw,
 *    so a 409 or a dropped connection renders inline next to the button that
 *    caused it.
 */
import { API_BASE_URL } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'
import { dataLayerSourceId } from '@/lib/dataLayerBase'
import { buildMessagesQuery, PAGE_LIMIT } from '@/lib/dataLayer/params'
import { DataLayerError, networkFailure, normalizeFailure, type DataLayerFailure } from '@/lib/dataLayer/errors'
import type {
  Hl7ChannelList,
  Hl7ClearResult,
  Hl7Fixture,
  Hl7FixtureList,
  Hl7Message,
  Hl7MessageFilter,
  Hl7MessageList,
  Hl7MimicRequest,
  Hl7MimicResult,
  Hl7ParseResult,
  Hl7SendRequest,
  Hl7SendResult,
  Hl7SourceList,
  Hl7Status,
} from '@/lib/dataLayer/types'

/** An action either produced a value or a renderable failure. It never throws. */
export type Outcome<T> = { ok: true; value: T } | { ok: false; failure: DataLayerFailure }

const READ_TIMEOUT_MS = 5_000
const MIMIC_TIMEOUT_MS = 10_000
/** A dead target burns timeoutMs * (retries + 1) in the harness before answering. */
const SEND_TIMEOUT_MS = 20_000

const PROXY_BASE = `${API_BASE_URL}/integrations/data-layer`

interface CallOptions {
  method?: string
  body?: unknown
  timeoutMs?: number
  /** A 404 here means the ring evicted the record, not that something broke. */
  evictable?: boolean
  /**
   * Name a source for this one call instead of the selected one. Only
   * `pingDataLayer` uses it, to probe a candidate before the user commits.
   */
  sourceId?: string
}

/** The selected source, or a thrown failure while none is known yet. Polls are
 * gated on a resolved source, so this firing means a hook lost its gate. */
function requireSourceId(): string {
  const id = dataLayerSourceId()
  if (id === null) {
    throw new DataLayerError({
      kind: 'http',
      status: 0,
      code: 'NO_SOURCE',
      message: 'No data-layer source selected yet.',
    })
  }
  return id
}

async function call<T>(path: string, options: CallOptions = {}): Promise<T> {
  const {
    method = 'GET',
    body,
    timeoutMs = READ_TIMEOUT_MS,
    evictable,
    // Resolved per call, never captured at module load: the Incoming tab can
    // switch sources at runtime.
    sourceId = requireSourceId(),
  } = options
  const url = `${PROXY_BASE}/${encodeURIComponent(sourceId)}${path}`

  let response: Response
  try {
    response = await authFetch(url, {
      method,
      headers: authHeaders(),
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: AbortSignal.timeout(timeoutMs),
    })
  } catch (error) {
    throw new DataLayerError(networkFailure(error))
  }

  // A 401 is OUR backend rejecting the session, not a data-layer state; end
  // the session like every other client. (The old direct-to-harness client
  // pinned the opposite, and that pin was correct until the proxy existed.)
  handleUnauthorized(response.status)

  const text = await response.text().catch(() => '')
  let parsed: unknown = null
  try {
    parsed = text ? JSON.parse(text) : null
  } catch {
    parsed = text
  }

  if (!response.ok) {
    throw new DataLayerError(normalizeFailure(response.status, parsed, { evictable }))
  }

  return parsed as T
}

/** Wraps a call so a component can render the failure instead of unmounting on it. */
async function attempt<T>(run: () => Promise<T>): Promise<Outcome<T>> {
  try {
    return { ok: true, value: await run() }
  } catch (error) {
    if (error instanceof DataLayerError) return { ok: false, failure: error.failure }
    return { ok: false, failure: networkFailure(error) }
  }
}

export const dataLayerApi = {
  // ── Reads. Throw DataLayerError; React Query renders the error state. ──────

  /**
   * The server-side allowlist: ids, labels, and which source is the default.
   * The one route with no `:sourceId` — it does not vary by source, so its
   * query key must NOT live under `['data-layer']` or a source switch would
   * clear it too.
   */
  listSources: async (): Promise<Hl7SourceList> => {
    let response: Response
    try {
      response = await authFetch(`${PROXY_BASE}/sources`, {
        headers: authHeaders(),
        signal: AbortSignal.timeout(READ_TIMEOUT_MS),
      })
    } catch (error) {
      throw new DataLayerError(networkFailure(error))
    }
    handleUnauthorized(response.status)
    if (!response.ok) {
      throw new DataLayerError(normalizeFailure(response.status, await response.json().catch(() => null)))
    }
    return response.json() as Promise<Hl7SourceList>
  },

  listChannels: () => call<Hl7ChannelList>('/channels'),

  getStatus: () => call<Hl7Status>('/status'),

  listMessages: (filter: Hl7MessageFilter = {}) =>
    call<Hl7MessageList>(`/messages${buildMessagesQuery({ limit: PAGE_LIMIT, ...filter })}`),

  /** 404 is normal here — the store is a bounded ring. Surfaces as `kind: 'evicted'`. */
  getMessage: (id: string) =>
    call<Hl7Message>(`/messages/${encodeURIComponent(id)}`, { evictable: true }),

  listFixtures: () => call<Hl7FixtureList>('/fixtures'),

  /** Restamps the control id and timestamp on every call. */
  getFixture: (name: string) => call<Hl7Fixture>(`/fixtures/${encodeURIComponent(name)}`),

  // ── Actions. Never throw; the panel renders `failure` inline. ──────────────

  /**
   * Clears BOTH directions — the store is one global buffer. Channel counters
   * are deliberately left alone, so `messages.in` keeps its value afterwards.
   */
  clearMessages: () => attempt(() => call<Hl7ClearResult>('/messages', { method: 'DELETE' })),

  /**
   * HTTP 200 with `ok: false` is a completed exchange the peer rejected, and
   * arrives as `{ok: true, value: {ok: false, reason}}` here. The outer ok is
   * transport, the inner one is the acknowledgment. 400/404/409 are the outer
   * failure.
   */
  send: (request: Hl7SendRequest) =>
    attempt(() =>
      call<Hl7SendResult>('/send', { method: 'POST', body: request, timeoutMs: SEND_TIMEOUT_MS }),
    ),

  /**
   * Injects a message as though a partner had sent it, over a real loopback MLLP
   * connection into the source's own listener — so framing, parsing, dispatch,
   * ACK and storage all run exactly as for a live arrival.
   *
   * Note it rejects an unparseable ER7 with 400 before dialling, so this cannot
   * produce a `msh: null` record.
   */
  mimicInbound: (request: Hl7MimicRequest) =>
    attempt(() =>
      call<Hl7MimicResult>('/mimic/inbound', {
        method: 'POST',
        body: request,
        timeoutMs: MIMIC_TIMEOUT_MS,
      }),
    ),

  /**
   * Probes a named source without selecting it. `/status` is the cheapest
   * route that proves a live data layer is behind the name, and its
   * `deployment` block says WHICH one.
   */
  pingDataLayer: (sourceId: string) =>
    attempt(() => call<Hl7Status>('/status', { sourceId })),

  /** Always answers 200, even for a malformed message — read `ok`. */
  parseHl7: (message: string) =>
    attempt(() => call<Hl7ParseResult>('/hl7/parse', { method: 'POST', body: { message } })),
}
